import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Send, CheckCircle, XCircle, Loader2, FileText, Edit, History, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminEmail = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_server: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    smtp_from_name: 'Carcortex-ai',
    app_url: 'https://carcortex-ai.com'
  });

  useEffect(() => {
    loadSmtpSettings();
    loadEmailTemplates();
    loadEmailLogs();
  }, []);

  const loadSmtpSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/email/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setSmtpConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der SMTP-Einstellungen:', error);
    }
  };

  const loadEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der E-Mail-Templates:', error);
    }
  };

  const loadEmailLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/email/logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogs(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der E-Mail-Logs:', error);
    }
  };

  const handleSave = async () => {
    if (!smtpConfig.smtp_server || !smtpConfig.smtp_username || !smtpConfig.smtp_password) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/email/smtp`, smtpConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('SMTP-Einstellungen erfolgreich gespeichert');
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      toast.error('Fehler beim Speichern der SMTP-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!smtpConfig.smtp_server || !smtpConfig.smtp_username || !smtpConfig.smtp_password) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus, bevor Sie testen');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/email/test`, smtpConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('SMTP-Verbindung erfolgreich getestet!');
      } else {
        toast.error('SMTP-Test fehlgeschlagen');
      }
    } catch (error) {
      console.error('Test fehlgeschlagen:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Verbindungstest fehlgeschlagen'
      });
      toast.error('SMTP-Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/email/templates/${template.id}`, template, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template erfolgreich gespeichert');
      loadEmailTemplates();
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Fehler beim Speichern des Templates');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  // Styles
  const cardStyle = {
    backgroundColor: '#1A1F2E',
    borderColor: '#2D3748',
    border: '1px solid',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '16px'
  };

  const inputStyle = {
    backgroundColor: '#2D3748',
    borderColor: '#4A5568',
    border: '1px solid',
    color: '#E2E8F0',
    padding: '8px 12px',
    borderRadius: '6px',
    width: '100%',
    fontSize: '14px'
  };

  const buttonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
    color: 'white'
  };

  const outlineButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: '1px solid #7CD4FD',
    color: '#7CD4FD'
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#0B0F14', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '30px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
            E-Mail-Verwaltung
          </h1>
          <p style={{ color: '#A0AEC0' }}>
            SMTP-Konfiguration, E-Mail-Templates und Versandhistorie
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '24px', padding: '4px', backgroundColor: '#1A1F2E', borderRadius: '8px' }}>
          <button
            onClick={() => setActiveTab('smtp')}
            style={{
              ...buttonStyle,
              backgroundColor: activeTab === 'smtp' ? '#7CD4FD' : 'transparent',
              color: activeTab === 'smtp' ? '#0B0F14' : '#E2E8F0',
              fontWeight: activeTab === 'smtp' ? 'bold' : 'normal',
            }}
          >
            <Mail size={16} />
            SMTP-Konfiguration
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            style={{
              ...buttonStyle,
              backgroundColor: activeTab === 'templates' ? '#7CD4FD' : 'transparent',
              color: activeTab === 'templates' ? '#0B0F14' : '#E2E8F0',
              fontWeight: activeTab === 'templates' ? 'bold' : 'normal',
            }}
          >
            <FileText size={16} />
            E-Mail-Templates
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            style={{
              ...buttonStyle,
              backgroundColor: activeTab === 'logs' ? '#7CD4FD' : 'transparent',
              color: activeTab === 'logs' ? '#0B0F14' : '#E2E8F0',
              fontWeight: activeTab === 'logs' ? 'bold' : 'normal',
            }}
          >
            <History size={16} />
            Versandhistorie
          </button>
        </div>

        {/* SMTP Tab */}
        {activeTab === 'smtp' && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
              SMTP-Server-Konfiguration
            </h2>
            <p style={{ color: '#A0AEC0', marginBottom: '24px' }}>
              Konfigurieren Sie Ihren SMTP-Server für automatische E-Mails
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  SMTP-Server *
                </label>
                <input
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={smtpConfig.smtp_server}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_server: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                    Port *
                  </label>
                  <input
                    type="number"
                    value={smtpConfig.smtp_port}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_port: e.target.value }))}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                    Verschlüsselung
                  </label>
                  <select
                    value={smtpConfig.smtp_encryption}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_encryption: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="tls">TLS (Port 587)</option>
                    <option value="ssl">SSL (Port 465)</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  Benutzername / E-Mail *
                </label>
                <input
                  type="email"
                  value={smtpConfig.smtp_username}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  Passwort *
                </label>
                <input
                  type="password"
                  value={smtpConfig.smtp_password}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  Absender-Name
                </label>
                <input
                  type="text"
                  value={smtpConfig.smtp_from_name}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              {testResult && (
                <div style={{
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: testResult.success ? '#1e4620' : '#4a1c1c',
                  border: `1px solid ${testResult.success ? '#2e7d32' : '#dc2626'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {testResult.success ? (
                    <CheckCircle size={16} style={{ color: '#4CAF50' }} />
                  ) : (
                    <XCircle size={16} style={{ color: '#ef4444' }} />
                  )}
                  <span style={{ color: testResult.success ? '#a5d6a7' : '#fca5a5' }}>
                    {testResult.message}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '16px', paddingTop: '16px' }}>
                <button
                  onClick={handleTestConnection}
                  disabled={testing || loading}
                  style={outlineButtonStyle}
                >
                  {testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Verbindung testen
                </button>

                <button
                  onClick={handleSave}
                  disabled={loading || testing}
                  style={primaryButtonStyle}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Einstellungen speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && !editingTemplate && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
              E-Mail-Templates
            </h2>
            <p style={{ color: '#A0AEC0', marginBottom: '24px' }}>
              Bearbeiten Sie die automatischen E-Mail-Vorlagen
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {emailTemplates.map((template) => (
                <div
                  key={template.id}
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: '#2D3748',
                    border: '1px solid #4A5568'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontWeight: '600', marginBottom: '4px', color: '#E2E8F0' }}>
                        {template.name}
                      </h3>
                      <p style={{ fontSize: '14px', marginBottom: '8px', color: '#A0AEC0' }}>
                        {template.description}
                      </p>
                      <div style={{ fontSize: '12px', color: '#94A3B8' }}>
                        Betreff: {template.subject}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingTemplate({...template})}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#7CD4FD',
                        color: '#0B0F14',
                        padding: '6px 12px',
                        fontSize: '13px'
                      }}
                    >
                      <Edit size={14} />
                      Bearbeiten
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '24px',
              padding: '16px',
              borderRadius: '6px',
              backgroundColor: '#1e3a5f',
              border: '1px solid #1EA0E6',
              display: 'flex',
              alignItems: 'start',
              gap: '12px'
            }}>
              <Plus size={16} style={{ color: '#7CD4FD', marginTop: '2px' }} />
              <div style={{ color: '#E2E8F0', fontSize: '14px' }}>
                <strong>Zukünftige Funktion:</strong> Benutzerdefinierte E-Mail-Regeln und automatisierte E-Mail-Kampagnen werden in Kürze verfügbar sein.
              </div>
            </div>
          </div>
        )}

        {/* Template Editor */}
        {activeTab === 'templates' && editingTemplate && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
              Template bearbeiten: {editingTemplate.name}
            </h2>
            <p style={{ color: '#A0AEC0', marginBottom: '24px' }}>
              Passen Sie den Betreff und Inhalt der E-Mail an
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  Betreff
                </label>
                <input
                  type="text"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#E2E8F0', fontSize: '14px' }}>
                  HTML-Inhalt
                </label>
                <textarea
                  value={editingTemplate.html_content}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, html_content: e.target.value })}
                  rows={15}
                  style={{
                    ...inputStyle,
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => setEditingTemplate(null)}
                  style={{
                    ...buttonStyle,
                    backgroundColor: 'transparent',
                    border: '1px solid #4A5568',
                    color: '#E2E8F0'
                  }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleSaveTemplate(editingTemplate)}
                  style={primaryButtonStyle}
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px', color: '#7CD4FD' }}>
              Versandhistorie
            </h2>
            <p style={{ color: '#A0AEC0', marginBottom: '24px' }}>
              Übersicht aller versendeten E-Mails (letzte 50)
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {emailLogs.length === 0 ? (
                <p style={{ color: '#A0AEC0' }}>Noch keine E-Mails versendet</p>
              ) : (
                emailLogs.map((log, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      borderRadius: '6px',
                      backgroundColor: '#2D3748',
                      border: '1px solid #4A5568'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#E2E8F0' }}>
                            {log.template_name}
                          </span>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: log.status === 'sent' ? '#1e4620' : '#4a1c1c',
                              color: log.status === 'sent' ? '#4CAF50' : '#ef4444'
                            }}
                          >
                            {log.status === 'sent' ? 'Gesendet' : 'Fehlgeschlagen'}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#A0AEC0' }}>
                          An: {log.to_email}
                        </div>
                        <div style={{ fontSize: '12px', marginTop: '4px', color: '#94A3B8' }}>
                          {formatDate(log.sent_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
